import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { hideSpinnersStyle } from './constants';
import { ChevronDown, ChevronRight, Lock, Phone, Mail, ExternalLink, Bookmark } from 'lucide-react';

const ITEM_TYPE_COLORS = {
  'Робота': '#22c55e',
  'Матеріали': '#eab308',
  'Комплектація': '#3b82f6',
  'Транспорт': '#f97316',
  'Інші послуги': '#a855f7'
};

const ITEM_TYPE_LETTERS = {
  'Робота': 'Р',
  'Матеріали': 'М',
  'Комплектація': 'К',
  'Транспорт': 'Т',
  'Інші послуги': 'І'
};

const formatPrice = (num) => (num || 0).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const DirectoryPublicView = ({ shareToken }) => {
  const isDark = (hex) => {
    if (!hex) return false;
    const c = hex.replace('#', '');
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 140;
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [brandSettings, setBrandSettings] = useState(null);
  const [sections, setSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadData();
  }, [shareToken]);

  const loadData = async () => {
    try {
      setLoading(true);

      // 1. Отримуємо share запис
      const { data: share, error: shareError } = await supabase
        .from('shared_directory')
        .select('*')
        .eq('share_token', shareToken)
        .single();

      if (shareError || !share) {
        setError('Посилання недійсне або прайс-лист не знайдено');
        setLoading(false);
        return;
      }

      setShareData(share);

      if (share.is_active === false) {
        setError('inactive');
        setLoading(false);
        return;
      }

      // 2. Завантажуємо brand settings
      try {
        const { data: brand } = await supabase
          .from('user_brand_settings')
          .select('settings')
          .eq('user_id', share.user_id)
          .single();
        if (brand?.settings) setBrandSettings(brand.settings);
      } catch (e) { /* no brand */ }

      // 3. Завантажуємо тільки публічні розділи. Опційний URL-фільтр ?s=key1,key2
      let secsQuery = supabase
        .from('user_directory')
        .select('*')
        .eq('user_id', share.user_id)
        .eq('is_private', false)
        .order('sort_order');
      const urlKeys = new URLSearchParams(window.location.search).get('s');
      if (urlKeys) {
        const keys = urlKeys.split(',').map(k => k.trim()).filter(Boolean);
        if (keys.length > 0) secsQuery = secsQuery.in('share_key', keys);
      }
      const { data: secs } = await secsQuery;

      // 4. Items лише для видимих розділів — приватні не леакаються в мережу
      const sectionIds = (secs || []).map(s => s.id);
      let itemsArr = [];
      if (sectionIds.length > 0) {
        const { data: it } = await supabase
          .from('user_directory_items')
          .select('*')
          .in('section_id', sectionIds)
          .order('sort_order');
        itemsArr = it || [];
      }

      const sectionsWithItems = (secs || []).map(s => ({
        ...s,
        items: itemsArr.filter(i => i.section_id === s.id)
      }));

      setSections(sectionsWithItems);

      // Розкриваємо всі секції за замовчуванням
      const expanded = {};
      sectionsWithItems.forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);

      setLoading(false);
    } catch (e) {
      console.error('Error:', e);
      setError('Помилка завантаження');
      setLoading(false);
    }
  };

  const totalItems = sections.reduce((sum, s) => sum + (s.items || []).length, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <style>{hideSpinnersStyle}</style>
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Завантаження...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <style>{hideSpinnersStyle}</style>
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-gray-400" />
          </div>
          {error === 'inactive' ? (
            <>
              <h1 className="text-lg font-semibold text-gray-900 mb-2">Прайс-лист недоступний</h1>
              <p className="text-sm text-gray-500">Власник тимчасово обмежив доступ до прайс-листа.</p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-gray-900 mb-2">Посилання недійсне</h1>
              <p className="text-sm text-gray-500">{error}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const filteredSections = sections.map(s => ({
    ...s,
    items: (s.items || []).filter(i => !search || i.position.toLowerCase().includes(search.toLowerCase()))
  })).filter(s => s.items.length > 0 || !search);

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{hideSpinnersStyle}</style>

      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="logo-text text-lg">LIHTAR</span>
            <span className="text-xs text-gray-400 hidden sm:inline">• прайс-лист</span>
          </div>
          <span className="text-xs text-gray-400">{totalItems} позицій</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4 sm:py-6 space-y-4">
        
        {/* Брендинг-блок */}
        {brandSettings && (brandSettings.logo || brandSettings.logoCropped || brandSettings.companyName) && (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="px-4 sm:px-6 py-4 sm:py-5">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4">
                {(brandSettings.logoCropped || brandSettings.logo) && (
                  <div className="flex-shrink-0 flex items-center justify-center w-full sm:w-[120px]">
                    <img src={brandSettings.logoCropped || brandSettings.logo} alt="" className="max-h-14 max-w-[180px] sm:max-w-[120px] object-contain" />
                  </div>
                )}
                <div className="flex-1 w-full text-center sm:text-right">
                  {brandSettings.companyName && (
                    <div className="text-sm sm:text-base font-semibold" style={{ color: '#163248' }}>{brandSettings.companyName}</div>
                  )}
                  <div className="flex flex-wrap items-center justify-center sm:justify-end gap-x-4 gap-y-1 mt-1 text-xs sm:text-sm text-gray-500">
                    {brandSettings.phone && (
                      <a href={`tel:${brandSettings.phone}`} className="flex items-center gap-1 hover:text-gray-700">
                        <Phone className="w-3 h-3" />{brandSettings.phone}
                      </a>
                    )}
                    {brandSettings.email && (
                      <a href={`mailto:${brandSettings.email}`} className="flex items-center gap-1 hover:text-gray-700">
                        <Mail className="w-3 h-3" />{brandSettings.email}
                      </a>
                    )}
                    {brandSettings.website && (
                      <a href={brandSettings.website.startsWith('http') ? brandSettings.website : `https://${brandSettings.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-gray-600">
                        <ExternalLink className="w-3 h-3" />{brandSettings.website.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Title card */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#fef3c7' }}>
                <Bookmark className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-xl font-bold" style={{ color: '#163248' }}>Прайс-лист</h1>
                <p className="text-sm text-gray-500 mt-0.5">{totalItems} позицій · {sections.length} розділів</p>
              </div>
            </div>
            {/* Search */}
            <div className="mt-4 relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Пошук позицій..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
        </div>

        {/* Sections */}
        {filteredSections.map((section, sIdx) => {
          const isOpen = expandedSections[section.id] !== false;
          return (
            <div key={section.id} className="bg-white rounded-xl border shadow-sm overflow-hidden">
              {/* Section header */}
              <div
                className="px-4 sm:px-6 py-3 cursor-pointer flex items-center justify-between"
                style={{ backgroundColor: brandSettings?.sectionBg || '#fde68a' }}
                onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isOpen ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: isDark(brandSettings?.sectionBg) ? 'rgba(255,255,255,0.6)' : '#6b7280' }} /> : <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: isDark(brandSettings?.sectionBg) ? 'rgba(255,255,255,0.6)' : '#6b7280' }} />}
                  <span className="font-semibold text-sm" style={{ color: isDark(brandSettings?.sectionBg) ? '#ffffff' : '#1f2937' }}>{sIdx + 1}. {section.name}</span>
                </div>
                <span className="text-xs flex-shrink-0" style={{ color: isDark(brandSettings?.sectionBg) ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>{section.items.length} поз.</span>
              </div>

              {/* Items */}
              {isOpen && (
                <div>
                  {/* Table header */}
                  <div className="hidden sm:flex items-center px-4 sm:px-6 py-2 bg-gray-50 border-b text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    <div style={{ width: 30 }}></div>
                    <div style={{ width: 40 }}>№</div>
                    <div className="flex-1">Позиція</div>
                    <div style={{ width: 50 }} className="text-center">Од.</div>
                    <div style={{ width: 90 }} className="text-right">Ціна</div>
                  </div>

                  {section.items.map((item, iIdx) => (
                    <div key={item.id} className="flex items-center px-4 sm:px-6 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                      {/* Type badge */}
                      <div style={{ width: 30 }} className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[9px] font-bold"
                          style={{ backgroundColor: ITEM_TYPE_COLORS[item.type] || '#22c55e' }}>
                          {ITEM_TYPE_LETTERS[item.type] || 'Р'}
                        </span>
                      </div>
                      {/* Number */}
                      <div style={{ width: 40 }} className="text-xs text-gray-500 flex-shrink-0">{sIdx + 1}.{iIdx + 1}</div>
                      {/* Position */}
                      <div className="flex-1 min-w-0 text-sm" style={{ color: '#374151' }}>
                        <span className="break-words">{item.position || '-'}</span>
                      </div>
                      {/* Unit */}
                      <div style={{ width: 50 }} className="text-center text-xs text-gray-400 flex-shrink-0">{item.unit || ''}</div>
                      {/* Price */}
                      <div style={{ width: 90, color: '#374151' }} className="text-right text-sm flex-shrink-0">
                        {formatPrice(item.price)} ₴
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {sections.length === 0 && (
          <div className="bg-white rounded-xl border shadow-sm px-6 py-8 text-center">
            <p className="text-sm text-gray-400">Власник поки не додав публічних позицій у довідник.</p>
          </div>
        )}
        {sections.length > 0 && filteredSections.length === 0 && search && (
          <div className="bg-white rounded-xl border shadow-sm px-6 py-8 text-center">
            <p className="text-sm text-gray-400">Нічого не знайдено за запитом "{search}"</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-[10px] text-gray-400 mb-1">Створено на платформі</p>
          <span className="logo-text text-base" style={{ color: brandSettings?.primaryColor || '#163248' }}>LIHTAR</span>
        </div>
      </div>
    </div>
  );
};

export default DirectoryPublicView;
